/**
 * ItemDetail — detalle de item reutilizable con 3 modos configurables por tipo de listado.
 *
 * Regla de negocio (Gabriel, 2026-08-07): al hacer click en una fila, el detalle puede abrirse de
 * 3 formas según el tipo de dato. Este archivo provee los 3, con nombres fáciles de referenciar:
 *
 *   1) <ItemDetailDrawer>          — CAJÓN INLINE debajo del item, dentro del listado.
 *      (modo 1)                       Trae X de cerrar y soporta EDITAR los campos en el MISMO cajón
 *                                     (el caller pasa el form como children). Ideal: Usuarios.
 *   2) <ItemDetailSplitPane>       — OVERLAY DENTRO DEL CONTENEDOR: panel a la derecha (50% ancho)
 *      (modo 2)                       que se SUPERPONE sobre el listado sin alterarlo. El parent solo
 *                                     debe ser `relative`; la lista queda `w-full` intacta debajo y
 *                                     este componente se posiciona `absolute inset-0` con un backdrop
 *                                     tenue. Click en el backdrop o Esc cierra. Ideal: Data Health / Ocultos.
 *   3) <ItemDetailSideDrawer>      — OVERLAY FULL-SCREEN: drawer fijo a la derecha de la pantalla con
 *      (modo 3)                       overlay oscuro (`fixed inset-0`, panel ~30% ancho). Click en el
 *                                     overlay o Esc cierra. Ideal: Data Health / Cola de Revisión.
 *
 * Ambos (modo 2 y 3) son OVERLAYS sobre el contenedor superior: no colapsan ni reflowean el listado,
 * lo dejan intacto debajo. La diferencia es el alcance: modo 2 se superpone dentro del contenedor del
 * listado (50%), modo 3 sobre toda la pantalla (~30%).
 *
 * Teclado: Esc cierra (modos 2 con `onEscClose` y 3); ↑/↓ = prev/next si se pasan los callbacks
 * (se ignoran mientras el foco está en input/textarea/select). El cajón (modo 1) deja la navegación
 * ↑/↓ al listado (useListKeyboardNav) para que el cajón siga al item seleccionado.
 *
 * Ver: docs/design-system/COMPONENT_CATALOG.md
 */
import React, { useEffect } from 'react';
import { X, ChevronUp, ChevronDown } from 'lucide-react';

export interface DetailTab {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
}

interface HeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
  /** Slot a la derecha del header, antes de prev/next (acciones: editar, reset, borrar). */
  headerActions?: React.ReactNode;
  onPrev?: () => void;
  onNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;
  onClose: () => void;
  dense?: boolean;
}

const DetailHeader: React.FC<HeaderProps> = ({
  title, subtitle, icon: Icon, headerActions, onPrev, onNext, hasPrev, hasNext, onClose, dense,
}) => (
  <div
    className={`${dense ? 'px-3 py-2' : 'px-4 py-3'} border-b border-gray-200 flex items-center justify-between gap-3 flex-shrink-0`}
  >
    <div className="flex items-center gap-2 min-w-0">
      {Icon && <Icon className="w-5 h-5 text-blue-600 flex-shrink-0" />}
      <div className="min-w-0">
        <h3 className="text-sm font-semibold text-gray-900 truncate">{title}</h3>
        {subtitle && <p className="text-xs text-gray-500 truncate">{subtitle}</p>}
      </div>
    </div>
    <div className="flex items-center gap-2 flex-shrink-0">
      {headerActions}
      {(onPrev || onNext) && (
        <div className="flex items-center border border-gray-200 rounded overflow-hidden">
          <button
            type="button"
            onClick={onPrev}
            disabled={!hasPrev}
            title="Item anterior (↑)"
            className="p-1.5 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={!hasNext}
            title="Item siguiente (↓)"
            className="p-1.5 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed border-l border-gray-200"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>
      )}
      <button
        type="button"
        onClick={onClose}
        title="Cerrar (Esc)"
        className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
      >
        <X className="w-5 h-5 text-gray-500" />
      </button>
    </div>
  </div>
);

// Tab list compartida por los 3 modos (opcional).
const TabList: React.FC<{
  tabs?: DetailTab[];
  activeTab?: string;
  onTabChange?: (id: string) => void;
}> = ({ tabs, activeTab, onTabChange }) => {
  if (!tabs || tabs.length === 0) return null;
  return (
    <div className="flex border-b border-gray-200 px-2 flex-shrink-0 overflow-x-auto">
      {tabs.map((tab) => {
        const TabIcon = tab.icon;
        const active = tab.id === activeTab;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onTabChange?.(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              active
                ? 'border-blue-700 text-blue-700 bg-blue-50'
                : 'border-transparent text-gray-700 hover:bg-gray-50 hover:text-gray-900'
            }`}
          >
            {TabIcon && <TabIcon className="w-3.5 h-3.5" />}
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};

// Hook de teclado compartido (modos 2 y 3): Esc cierra, ↑/↓ prev/next.
function useDetailKeyboard(
  enabled: boolean,
  onClose: () => void,
  opts: { onPrev?: () => void; onNext?: () => void; hasPrev?: boolean; hasNext?: boolean; onEsc?: boolean }
) {
  const { onPrev, onNext, hasPrev, hasNext, onEsc = true } = opts;
  useEffect(() => {
    if (!enabled) return;
    const handler = (e: KeyboardEvent) => {
      const el = document.activeElement;
      const tag = el?.tagName?.toLowerCase();
      const typing = tag === 'input' || tag === 'textarea' || tag === 'select';
      if (onEsc && e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (!typing && e.key === 'ArrowUp' && hasPrev && onPrev) {
        e.preventDefault();
        onPrev();
      } else if (!typing && e.key === 'ArrowDown' && hasNext && onNext) {
        e.preventDefault();
        onNext();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [enabled, onClose, onPrev, onNext, hasPrev, hasNext, onEsc]);
}

// ---------------- Modo 1 — Cajón inline (ItemDetailDrawer) ----------------

interface DrawerProps extends Omit<HeaderProps, 'dense'> {
  children: React.ReactNode;
  className?: string;
}

/**
 * Cajón inline. Pensado para renderizarse JUSTO debajo del item seleccionado dentro del listado.
 * En una <table>, envolverlo en `<tr><td colSpan={N}>...</td></tr>` para que ocupe el ancho completo.
 * La navegación ↑/↓ la maneja el listado (useListKeyboardNav), no el cajón.
 */
export const ItemDetailDrawer: React.FC<DrawerProps> = ({
  title,
  subtitle,
  icon,
  headerActions,
  onPrev,
  onNext,
  hasPrev = false,
  hasNext = false,
  onClose,
  children,
  className = '',
}) => {
  return (
    <div className={`rounded-lg border border-blue-200 bg-white shadow-sm my-1 ${className}`}>
      <DetailHeader
        dense
        title={title}
        subtitle={subtitle}
        icon={icon}
        headerActions={headerActions}
        onPrev={onPrev}
        onNext={onNext}
        hasPrev={hasPrev}
        hasNext={hasNext}
        onClose={onClose}
      />
      <div className="p-4">{children}</div>
    </div>
  );
};

// ---------------- Modo 2 — Panel lateral (ItemDetailSplitPane) ----------------

interface SplitPaneProps extends Omit<HeaderProps, 'dense'> {
  tabs?: DetailTab[];
  activeTab?: string;
  onTabChange?: (id: string) => void;
  /** Ancho del panel overlay (clase Tailwind). Default w-1/2, como Data Health / Ocultos. */
  width?: string;
  /** Habilita cierre con Esc. Default true. En split-pane a veces se prefiere false. */
  escToClose?: boolean;
  /** Slot fijo al pie del panel (acciones). */
  footer?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Overlay dentro del contenedor. Se SUPERPONE sobre el listado a la derecha (50% ancho) sin alterarlo.
 *
 * IMPORTANTE — el PARENT solo necesita ser `relative` y NO debe colapsar la lista:
 *   <div className="relative flex-1 border border-gray-200 rounded overflow-hidden bg-white">
 *     <div className="w-full overflow-auto">{tabla}</div>   // queda intacta, w-full
 *     {open && <ItemDetailSplitPane ... />}                  // overlay absolute encima
 *   </div>
 * Este componente renderiza un backdrop tenue (absolute inset-0) + el panel pegado a la derecha.
 * Click en el backdrop o Esc cierra. La lista debajo nunca se reflowea.
 */
export const ItemDetailSplitPane: React.FC<SplitPaneProps> = ({
  title,
  subtitle,
  icon,
  headerActions,
  tabs,
  activeTab,
  onTabChange,
  onPrev,
  onNext,
  hasPrev = false,
  hasNext = false,
  onClose,
  width = 'w-1/2',
  escToClose = true,
  footer,
  children,
}) => {
  useDetailKeyboard(true, onClose, { onPrev, onNext, hasPrev, hasNext, onEsc: escToClose });

  return (
    <div
      className="absolute inset-0 z-20 flex justify-end bg-black bg-opacity-10"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={`${width} border-l border-gray-200 flex flex-col bg-white shadow-xl overflow-hidden`}>
        <DetailHeader
          title={title}
          subtitle={subtitle}
          icon={icon}
          headerActions={headerActions}
          onPrev={onPrev}
          onNext={onNext}
          hasPrev={hasPrev}
          hasNext={hasNext}
          onClose={onClose}
        />
        <TabList tabs={tabs} activeTab={activeTab} onTabChange={onTabChange} />
        <div className="flex-1 overflow-y-auto p-4">{children}</div>
        {footer && <div className="border-t border-gray-200 px-4 py-3 flex-shrink-0">{footer}</div>}
      </div>
    </div>
  );
};

// ---------------- Modo 3 — Panel deslizante (ItemDetailSideDrawer) ----------------

interface SideDrawerProps extends Omit<HeaderProps, 'dense'> {
  open: boolean;
  tabs?: DetailTab[];
  activeTab?: string;
  onTabChange?: (id: string) => void;
  /** Ancho del panel (clase Tailwind). Default ~30% pantalla, como Data Health / Cola de Revisión. */
  maxWidth?: string;
  /** Slot fijo al pie del panel (acciones: revivir/confirmar/etc.). */
  footer?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Panel deslizante. Drawer fijo a la derecha de toda la pantalla, con overlay oscuro.
 * Click en el overlay o Esc cierra. Trae footer opcional para acciones (como Cola de Revisión).
 */
export const ItemDetailSideDrawer: React.FC<SideDrawerProps> = ({
  open,
  title,
  subtitle,
  icon,
  headerActions,
  tabs,
  activeTab,
  onTabChange,
  onPrev,
  onNext,
  hasPrev = false,
  hasNext = false,
  onClose,
  maxWidth = 'w-full sm:w-[30%] sm:min-w-[440px]',
  footer,
  children,
}) => {
  useDetailKeyboard(open, onClose, { onPrev, onNext, hasPrev, hasNext });

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-black bg-opacity-30"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={`bg-white ${maxWidth} shadow-xl border-l border-gray-200 flex flex-col`}>
        <DetailHeader
          title={title}
          subtitle={subtitle}
          icon={icon}
          headerActions={headerActions}
          onPrev={onPrev}
          onNext={onNext}
          hasPrev={hasPrev}
          hasNext={hasNext}
          onClose={onClose}
        />
        <TabList tabs={tabs} activeTab={activeTab} onTabChange={onTabChange} />
        <div className="flex-1 overflow-y-auto p-4">{children}</div>
        {footer && <div className="border-t border-gray-200 px-4 py-3 flex-shrink-0">{footer}</div>}
      </div>
    </div>
  );
};
